import React, { useRef, useState, useEffect, useCallback } from "react";
import { galleryItems } from "../data/gallery";
import { ExternalLink, ImageOff, PlayCircle, Play, Lock, X, Maximize2, MonitorPlay } from "lucide-react";

const VideoCard = ({ item }) => {
  const ref = useRef(null);
  return (
    <div data-testid="gallery-item-video" className="card group relative overflow-hidden"
      onMouseEnter={() => ref.current && ref.current.play().catch(() => {})}
      onMouseLeave={() => ref.current && ref.current.pause()}>
      {item.src ? (
        <video ref={ref} src={item.src} poster={item.poster || undefined} muted loop playsInline preload="metadata"
          className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : <Placeholder icon={<PlayCircle size={28} strokeWidth={1.4} />} label="Hover-to-play video" />}
      {item.src && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 group-hover:opacity-0">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/25" style={{ backdropFilter: "blur(4px)" }}>
            <Play size={24} className="ml-1 text-gold" fill="currentColor" />
          </span>
        </div>
      )}
      <Caption title={item.title} sub="Video · hover to play" />
    </div>
  );
};

const PhotoCard = ({ item }) => (
  <div data-testid="gallery-item-photo" className="card group relative overflow-hidden">
    {item.src ? (
      <img src={item.src} alt={item.title} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : <Placeholder icon={<ImageOff size={28} strokeWidth={1.4} />} label="Photo" />}
    <Caption title={item.title} sub={item.caption || "Photo"} />
  </div>
);

const WebsiteCard = ({ item }) => (
  <a data-testid="gallery-item-website" href={item.url} target="_blank" rel="noopener noreferrer" className="card group relative block overflow-hidden">
    {item.thumbnail ? (
      <img src={item.thumbnail} alt={item.title} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : (
      <div className="flex h-72 w-full items-center justify-center bg-[var(--elevated)]">
        <span className="font-head text-4xl italic text-gold">{safeHost(item.url)}</span>
      </div>
    )}
    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/90 to-transparent p-5">
      <div>
        <p className="text-sm text-[var(--text)]">{item.title}</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Live Website</p>
      </div>
      <ExternalLink size={18} className="text-gold transition-transform group-hover:translate-x-1" />
    </div>
  </a>
);

const safeHost = (u) => { try { return new URL(u).hostname.replace("www.", ""); } catch { return "Visit site"; } };

const PrivateCard = ({ item }) => (
  <div data-testid="gallery-item-private" className="card group relative overflow-hidden">
    {item.src ? (
      <img src={item.src} alt="Private website" aria-hidden className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : <div className="h-72 w-full bg-[var(--elevated)]" />}
    <div className="absolute inset-0 bg-black/50" style={{ backdropFilter: "blur(2px)" }} />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/20" style={{ backdropFilter: "blur(4px)" }}>
        <Lock size={22} className="text-gold" strokeWidth={1.6} />
      </span>
    </div>
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
      <p className="text-sm text-[var(--text)]">{item.title}</p>
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Private Website</p>
    </div>
  </div>
);

const InspirationCard = ({ item, onOpen }) => (
  <div data-testid="gallery-item-inspiration" onClick={onOpen} className="card group relative cursor-pointer overflow-hidden">
    {item.thumbnail ? (
      <img src={item.thumbnail} alt={item.title} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : (
      <div className="flex h-72 w-full items-center justify-center bg-[var(--elevated)]">
        <MonitorPlay size={40} className="text-gold" strokeWidth={1.2} />
      </div>
    )}
    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/90 to-transparent p-5">
      <div>
        <p className="text-sm text-[var(--text)]">{item.title}</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Inspiration Library · click to explore</p>
      </div>
      <Maximize2 size={18} className="text-gold transition-transform group-hover:scale-110" />
    </div>
  </div>
);

const SiteDialog = ({ item, onClose }) => {
  const escHandler = useCallback((e) => { if (e.key === "Escape") onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener("keydown", escHandler);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", escHandler); document.body.style.overflow = ""; };
  }, [escHandler]);
  return (
    <div data-testid="site-dialog" onClick={onClose}
      className="fixed inset-0 z-[999] flex flex-col bg-black/90 p-3 sm:p-6"
      style={{ backdropFilter: "blur(10px)", animation: "abcFade 0.25s ease" }}>
      <div onClick={(e) => e.stopPropagation()} className="mx-auto flex h-full w-full max-w-6xl flex-col">
        <div className="flex items-center justify-between pb-3">
          <div>
            <p className="text-sm text-[var(--text)]">{item.title}</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Inspiration Library</p>
          </div>
          <button data-testid="site-dialog-close" onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-[var(--text)] transition-colors hover:bg-white/20"
            aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="relative flex-1 overflow-hidden rounded-sm border border-[var(--border)] bg-[var(--surface)]">
          <iframe src={item.url} title={item.title} className="h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" />
        </div>
      </div>
    </div>
  );
};

const Placeholder = ({ icon, label }) => (
  <div className="flex h-72 w-full flex-col items-center justify-center gap-3 bg-[var(--elevated)] text-[var(--muted)]">
    <span className="text-gold">{icon}</span>
    <span className="text-xs uppercase tracking-[0.25em]">{label} — replace me</span>
  </div>
);

const Caption = ({ title, sub }) => (
  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
    <p className="text-sm text-[var(--text)]">{title}</p>
    <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{sub}</p>
  </div>
);

export default function Gallery() {
  const [site, setSite] = useState(null);
  const hasItems = galleryItems && galleryItems.length > 0;
  return (
    <section data-testid="gallery-section" className="px-6 pb-40">
      <div className="mx-auto max-w-7xl">
        {hasItems ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {galleryItems.map((item, i) => {
              if (item.type === "video") return <VideoCard key={i} item={item} />;
              if (item.type === "website") return <WebsiteCard key={i} item={item} />;
              if (item.type === "private") return <PrivateCard key={i} item={item} />;
              if (item.type === "inspiration") return <InspirationCard key={i} item={item} onOpen={() => setSite(item)} />;
              return <PhotoCard key={i} item={item} />;
            })}
          </div>
        ) : (
          <div data-testid="gallery-empty" className="border border-[var(--border)] bg-[rgba(15,15,15,0.6)] p-20 text-center" style={{ backdropFilter: "blur(16px)" }}>
            <p className="font-head text-3xl font-light text-[var(--text)]">Confidential Projects in Development</p>
            <p className="mx-auto mt-4 max-w-md text-sm text-[var(--text-2)]">
              Finished work appears here as it ships — real projects only. An elegant empty gallery beats a fabricated one.
            </p>
          </div>
        )}
      </div>
      {site && <SiteDialog item={site} onClose={() => setSite(null)} />}
    </section>
  );
}
